import { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useGetAnnualReportMetricsQuery } from '../../../app/lib/apiSlice/reportsSlice/reportSlice.tsx';

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function PropertyChart() {
    const currentYear = new Date().getFullYear();
    const [activeYear, setActiveYear] = useState(currentYear.toString());
    const [years, setYears] = useState([]);
    const [series, setSeries] = useState([
        { name: 'Rent Collection', data: Array(12).fill(0), color: '#A855F7' },
        { name: 'Property Expenses', data: Array(12).fill(0), color: '#F97316' },
        { name: 'Profit', data: Array(12).fill(0), color: '#10B981' }
    ]);

    // Generate years array (current year and 5 years back)
    useEffect(() => {
        const yearsArray = [];
        for (let i = 0; i < 6; i++) {
            yearsArray.push((currentYear - i).toString());
        }
        setYears(yearsArray);
    }, [currentYear]);

    const { data: propertiesMetrics } = useGetAnnualReportMetricsQuery({
        year: activeYear,
    });

    // Prepare data from API response
    useEffect(() => {
        if (propertiesMetrics?.monthly_metrics) {
            // Initialize arrays with zeros for all months
            const rent = Array(12).fill(0);
            const expenses = Array(12).fill(0);
            const profit = Array(12).fill(0);

            // Fill in data from API response
            propertiesMetrics.monthly_metrics.forEach(metric => {
                const monthIndex = monthNames.indexOf(metric.month);
                if (monthIndex !== -1) {
                    rent[monthIndex] = metric.rent_collection;
                    expenses[monthIndex] = metric.property_expenses;
                    profit[monthIndex] = metric.profit;
                }
            });

            setSeries([
                {
                    name: 'Rent Collection',
                    data: rent,
                    color: '#A855F7'
                },
                {
                    name: 'Property Expenses',
                    data: expenses,
                    color: '#F97316'
                },
                {
                    name: 'Profit',
                    data: profit,
                    color: '#10B981'
                }
            ]);
        }
    }, [propertiesMetrics]);

    const options = {
        chart: {
            type: 'bar',
            height: 350,
            stacked: false,
            toolbar: {
                show: true
            },
            zoom: {
                enabled: true
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4,
            },
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        xaxis: {
            categories: shortMonthNames,
            labels: {
                style: {
                    colors: '#6B7280',
                    fontFamily: 'Inter, sans-serif'
                }
            },
            title: {
                text: 'Months',
                style: {
                    color: '#374151',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 'bold'
                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                formatter: function (value) {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'KES',
                        maximumFractionDigits: 0
                    }).format(value);
                },
                style: {
                    colors: '#6B7280',
                    fontFamily: 'Inter, sans-serif'
                }
            },
            title: {
                text: 'Amount (KES)',
                style: {
                    color: '#374151',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 'bold'
                }
            },
            grid: {
                borderColor: '#E5E7EB',
                strokeDashArray: 4
            }
        },
        fill: {
            opacity: 1
        },
        tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            style: {
                fontFamily: 'Inter, sans-serif'
            },
            y: {
                formatter: function (value) {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'KES',
                        maximumFractionDigits: 0
                    }).format(value);
                }
            },
            custom: function({ series, seriesIndex, dataPointIndex, w }) {
                const rent = series[0][dataPointIndex];
                const expenses = series[1][dataPointIndex];
                const profit = series[2][dataPointIndex];
                const profitMargin = rent > 0 ? Math.round((profit / rent) * 100) : 0;
                
                return `
                    <div class="bg-gray-800 shadow-lg p-3 rounded-lg text-white apexcharts-tooltip-custom">
                        <div class="font-bold">${monthNames[dataPointIndex]} ${activeYear}</div>
                        <div class="flex justify-between mt-2">
                            <span>Rent Collection:</span>
                            <span class="ml-2 font-medium">${new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'KES',
                                maximumFractionDigits: 0
                            }).format(rent)}</span>
                        </div>
                        <div class="flex justify-between">
                            <span>Property Expenses:</span>
                            <span class="ml-2 font-medium">${new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'KES',
                                maximumFractionDigits: 0
                            }).format(expenses)}</span>
                        </div>
                        <div class="flex justify-between">
                            <span>Profit:</span>
                            <span class="ml-2 font-medium">${new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'KES',
                                maximumFractionDigits: 0
                            }).format(profit)}</span>
                        </div>
                        <hr class="my-2 border-gray-600">
                        <div class="flex justify-between">
                            <span>Profit Margin:</span>
                            <span class="ml-2 font-medium">${profitMargin}%</span>
                        </div>
                    </div>
                `;
            }
        },
        legend: {
            position: 'top',
            horizontalAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            markers: {
                radius: 12
            },
            itemMargin: {
                horizontal: 10,
                vertical: 5
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                legend: {
                    position: 'bottom'
                }
            }
        }]
    };

    return (
        <div className='bg-white shadow-xl mb-5 p-3 rounded-lg'>
            {/* Chart Visualization */}
            <div className="mb-8 p-6 rounded-xl">
                <div className="flex sm:flex-row flex-col justify-between items-start sm:items-center gap-4 mb-6">
                    <h3 className="font-medium text-gray-700 text-lg">Annual Property Performance ({activeYear})</h3>
                    <div className="flex space-x-4">
                        <span className="flex items-center text-sm">
                            <span className="bg-purple-500 mr-2 rounded-full w-3 h-3"></span>
                            Rent
                        </span>
                        <span className="flex items-center text-sm">
                            <span className="bg-orange-500 mr-2 rounded-full w-3 h-3"></span>
                            Expenses
                        </span>
                        <span className="flex items-center text-sm">
                            <span className="bg-emerald-500 mr-2 rounded-full w-3 h-3"></span>
                            Profit
                        </span>
                    </div>
                </div>
                
                {/* Year Selector */}
                <div className="flex justify-between items-center bg-white mb-6">
                    <div className="flex pb-2 overflow-x-auto">
                        {years.map((year) => (
                            <button
                                key={year}
                                onClick={() => setActiveYear(year)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap mr-2 transition-colors ${
                                    activeYear === year
                                        ? 'bg-purple-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {year}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chart Area */}
                <div className="relative h-96">
                    <ReactApexChart 
                        options={options} 
                        series={series} 
                        type="bar" 
                        height="100%"
                    />
                </div>
            </div>
        </div>
    );
}
